import type { TreeNode } from "./build-tag-tree";

export interface FolderOption {
  label: string;
  /** The first parent folder the label appears under, shown as a hint. */
  parent?: string;
}

export const FOLDER_SUGGESTION_LIMIT = 6;

/** Every folder in the tree once; a label nested under several parents keeps the first parent it was found under. */
export const flattenFolders = (tree: readonly TreeNode[]): FolderOption[] => {
  const folders = new Map<string, FolderOption>();
  const visit = (nodes: readonly TreeNode[], parent?: string) => {
    for (const node of nodes) {
      if (!folders.has(node.data.label)) {
        folders.set(node.data.label, { label: node.data.label, parent });
      }
      visit(node.children, node.data.label);
    }
  };
  visit(tree);
  return [...folders.values()];
};

const rank = (label: string, query: string) => {
  if (label === query) {
    return 0;
  }
  const normalized = label.toLowerCase();
  if (normalized === query.toLowerCase()) {
    return 1;
  }
  return normalized.startsWith(query.toLowerCase()) ? 2 : 3;
};

/** Folders whose name contains the query, case-insensitively: exact matches first, then prefixes, then the rest. */
export const folderSuggestions = (
  folders: readonly FolderOption[],
  query: string,
  limit = FOLDER_SUGGESTION_LIMIT
): FolderOption[] => {
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }
  return folders
    .filter(({ label }) => label.toLowerCase().includes(trimmed.toLowerCase()))
    .map((folder, index) => ({ folder, index, rank: rank(folder.label, trimmed) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, limit)
    .map(({ folder }) => folder);
};
